import React, { useCallback, useEffect, useState } from 'react';
import { Button, Form, Spinner } from 'react-bootstrap';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical, LockFill } from 'react-bootstrap-icons';
import { toast } from 'react-toastify';
import { shiftAPI } from '../../services';

/**
 * #133908 — "Bộ yếu tố đề xuất ca của bot" (lệnh sếp 13:30 11/09/2026).
 *
 * Ba phần, đúng 3 nhóm đã bóc từ mã nguồn:
 *  - Nhóm A: ràng buộc CỨNG, chỉ đọc — để quản lý thấy đủ bức tranh, không có control nào.
 *  - Nhóm B: 2 công tắc đã có sẵn trong hệ thống (đọc/ghi ĐÚNG 2 khoá cấu hình gốc).
 *  - Nhóm C: 4 yếu tố mềm — bật/tắt + KÉO-THẢ đổi thứ tự ưu tiên.
 * Chốt xong thì sinh các ĐỀ XUẤT 1..5 (lệnh sếp #731600508/#731600509/#731600510): THỨ TỰ ƯU TIÊN
 * GIỮ NGUYÊN ở mọi đề xuất. Ưu tiên cao nhất là các đề xuất GIỮ ĐỦ yếu tố, khác nhau ở CÁCH XẾP
 * NGƯỜI trong nhóm ngang điểm; chỉ khi vẫn thiếu đề xuất mới lược bớt từ yếu tố ÍT QUAN TRỌNG NHẤT.
 *
 * Kéo-thả theo luật "điện thoại trước" (#232509): dùng @dnd-kit đã có sẵn trong dự án
 * (BankAccountManagementPage đang dùng), tay cầm 44×44px, TouchSensor giữ 180ms để ngón tay
 * vẫn vuốt cuộn trang được bình thường.
 */

const SortableFactorRow = ({ factor, index, onToggle, disabled }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: factor.key,
  });

  return (
    <div
      ref={setNodeRef}
      data-testid={`bot-factor-row-${factor.key}`}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.75 : 1,
      }}
      className={`d-flex align-items-center gap-2 rounded border bg-white mb-2 p-2 ${
        factor.enabled ? '' : 'text-muted'
      }`}
    >
      <span
        {...attributes}
        {...listeners}
        data-testid={`bot-factor-handle-${factor.key}`}
        aria-label={`Kéo để đổi thứ tự ưu tiên: ${factor.label}`}
        style={{
          minWidth: 44,
          minHeight: 44,
          touchAction: 'none',
          cursor: 'grab',
        }}
        className="d-flex align-items-center justify-content-center text-secondary"
      >
        <GripVertical size={20} />
      </span>
      <span className="badge bg-secondary">{index + 1}</span>
      <div className="flex-grow-1 small">
        {factor.label}
        {!factor.enabled && <div className="text-muted">Đang tắt — bot bỏ qua tiêu chí này.</div>}
      </div>
      <Form.Check
        type="switch"
        id={`bot-factor-switch-${factor.key}`}
        data-testid={`bot-factor-switch-${factor.key}`}
        checked={!!factor.enabled}
        disabled={disabled}
        onChange={() => onToggle(factor.key)}
        aria-label={`Bật hoặc tắt yếu tố: ${factor.label}`}
      />
    </div>
  );
};

const BotFactorControlPanel = ({ branchId, onSaved }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState(null);
  const [factors, setFactors] = useState([]);
  const [toggles, setToggles] = useState({
    block_new_in_high_revenue: true,
    revenue_priority_enabled: true,
  });
  const [collapsed, setCollapsed] = useState(true);

  const sensors = useSensors(
    // 6px mới tính là kéo (đỡ nhầm với cú bấm công tắc); giữ 180ms mới kéo trên cảm ứng
    // để vuốt dọc vẫn cuộn được trang như thường.
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 180, tolerance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await shiftAPI.getBotFactorSettings({ branchId });
      const data = res?.data || res || null;
      setSettings(data);
      setFactors(data?.factors || []);
      if (data?.toggles) setToggles(data.toggles);
    } catch (error) {
      toast.error(error?.message || 'Không tải được bộ yếu tố đề xuất của bot');
      setSettings(null);
      setFactors([]);
    } finally {
      setLoading(false);
    }
  }, [branchId]);

  useEffect(() => {
    load();
  }, [load]);

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = factors.findIndex((item) => item.key === active.id);
    const newIndex = factors.findIndex((item) => item.key === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    // Đổi chỗ ngay trên màn; chỉ ghi xuống máy chủ khi bấm "Chốt bộ yếu tố".
    setFactors(arrayMove(factors, oldIndex, newIndex));
  };

  const toggleFactor = (key) => {
    setFactors((prev) =>
      prev.map((item) => (item.key === key ? { ...item, enabled: !item.enabled } : item)),
    );
  };

  const save = async () => {
    try {
      setSaving(true);
      const res = await shiftAPI.saveBotFactorSettings({
        branchId,
        factors: factors.map((item) => ({ key: item.key, enabled: !!item.enabled })),
        blockNewInHighRevenue: !!toggles.block_new_in_high_revenue,
        revenuePriorityEnabled: !!toggles.revenue_priority_enabled,
      });
      const data = res?.data || res || null;
      if (data) {
        setSettings(data);
        setFactors(data.factors || []);
        if (data.toggles) setToggles(data.toggles);
      }
      toast.success('Đã chốt bộ yếu tố — đang tạo lại phương án đề xuất');
      if (onSaved) await onSaved();
    } catch (error) {
      toast.error(error?.message || 'Không lưu được bộ yếu tố');
    } finally {
      setSaving(false);
    }
  };

  const enabledCount = factors.filter((item) => item.enabled).length;

  return (
    <div className="mb-3 rounded border bg-light p-3" data-testid="bot-factor-panel">
      <div className="mb-2 d-flex flex-wrap align-items-center justify-content-between gap-2">
        <div className="d-flex align-items-center gap-2">
          <span className="fw-semibold">Bộ yếu tố đề xuất ca của bot</span>
          <Button
            data-testid="bot-factor-toggle"
            variant="link"
            size="sm"
            className="p-0 text-decoration-none"
            aria-expanded={!collapsed}
            onClick={() => setCollapsed((v) => !v)}
          >
            {collapsed ? 'Mở rộng ▾' : 'Thu gọn ▴'}
          </Button>
        </div>
        <Button
          data-testid="bot-factor-save"
          variant="primary"
          size="sm"
          disabled={saving || loading || !factors.length}
          onClick={save}
        >
          {saving ? 'Đang chốt...' : 'Chốt bộ yếu tố & tạo phương án'}
        </Button>
      </div>

      {loading && (
        <div className="small text-muted">
          <Spinner animation="border" size="sm" className="me-2" />
          Đang tải bộ yếu tố...
        </div>
      )}

      {!collapsed && !loading && settings && (
        <>
          <div className="mb-3">
            <div className="small fw-semibold mb-1">
              <LockFill size={12} className="me-1" />
              Luôn áp dụng, không tắt được
            </div>
            {(settings.hard_constraints || []).map((row) => (
              <div
                key={row.code}
                data-testid={`bot-hard-constraint-${row.code}`}
                className="small text-muted border-bottom py-1"
              >
                <span className="fw-semibold text-dark">{row.label}</span> — {row.reason}
              </div>
            ))}
          </div>

          <div className="mb-3">
            <div className="small fw-semibold mb-1">Công tắc luật ca đông</div>
            <Form.Check
              type="switch"
              id="bot-toggle-revenue-priority"
              data-testid="bot-toggle-revenue_priority_enabled"
              className="small"
              checked={!!toggles.revenue_priority_enabled}
              onChange={() =>
                setToggles((prev) => ({
                  ...prev,
                  revenue_priority_enabled: !prev.revenue_priority_enabled,
                }))
              }
              /* #201334 — ca đông nay xét số ca trước; doanh thu chỉ phân xử khi đã ngang số ca. */
              label="Ở ca đông, khi hai người đã NGANG số ca thì ưu tiên người có doanh thu làm nước cao hơn (người ít ca hơn luôn được xếp trước)"
            />
            <Form.Check
              type="switch"
              id="bot-toggle-block-new"
              data-testid="bot-toggle-block_new_in_high_revenue"
              className="small"
              checked={!!toggles.block_new_in_high_revenue}
              onChange={() =>
                setToggles((prev) => ({
                  ...prev,
                  block_new_in_high_revenue: !prev.block_new_in_high_revenue,
                }))
              }
              label="Không để bot tự đề xuất nhân viên mới vào ca đông (quản lý duyệt tay)"
            />
          </div>

          <div>
            <div className="small fw-semibold mb-1">
              Thứ tự ưu tiên — kéo tay cầm để đổi, tắt công tắc để bỏ hẳn tiêu chí
            </div>
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
              <SortableContext
                items={factors.map((item) => item.key)}
                strategy={verticalListSortingStrategy}
              >
                <div>
                  {factors.map((factor, index) => (
                    <SortableFactorRow
                      key={factor.key}
                      factor={factor}
                      index={index}
                      onToggle={toggleFactor}
                      disabled={saving}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
            <div className="small text-muted" data-testid="bot-factor-expected">
              {`Đang bật ${enabledCount}/4 yếu tố — chốt xong sẽ tạo tối đa ${
                settings.expected_options || settings.max_options || 5
              } đề xuất (Đề xuất 1, 2, 3...). Thứ tự ưu tiên GIỮ NGUYÊN ở mọi đề xuất: bot tạo khác biệt bằng cách đổi lượt chọn giữa những người NGANG ĐIỂM nhau trước, chỉ khi vẫn chưa đủ đề xuất mới lược bớt dần yếu tố ít quan trọng nhất.`}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default BotFactorControlPanel;
